import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { P } from "const/permissions";
import { usePermission } from "hooks/usePermissions";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { fetchClientReferralState, setApplicationVisibility } from "./referralApplicationApi";

const EVENT = "kothar:prompt-referral-share";

/**
 * Asks "show this in the referral portal?" after an office create, from anywhere.
 *
 * Create forms call {@link promptReferralShare} with the new entry's coordinates; the host below
 * (mounted once in the admin layout, so it survives the navigate-to-edit that follows most
 * creates) checks whether the client actually has a referrer and, only then, shows the confirm
 * dialog. Entries start hidden, so dismissing keeps them hidden — the Applications tab toggle
 * remains the way to change it later.
 */
export const promptReferralShare = ({ module, entryId, clientId }) => {
  if (!module || !entryId || !clientId) return;
  window.dispatchEvent(new CustomEvent(EVENT, { detail: { module, entryId, clientId } }));
};

const MODULE_LABELS = {
  admission: "admission",
  visa: "visa",
  insurance: "insurance",
  "skill-assessment": "skill assessment",
  "rpl-course": "RPL",
};

export const ReferralSharePromptHost = () => {
  const canReview = usePermission(P.REFERRAL.APPLICATION_REVIEW);
  const [pending, setPending] = useState(null);
  const [client, setClient] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onPrompt = (event) => setPending(event?.detail ?? null);
    window.addEventListener(EVENT, onPrompt);
    return () => window.removeEventListener(EVENT, onPrompt);
  }, []);

  useEffect(() => {
    if (!pending || !canReview) {
      if (pending && !canReview) setPending(null);
      return;
    }
    let cancelled = false;
    fetchClientReferralState(pending.clientId)
      .then((state) => {
        if (cancelled) return;
        if (state?.referralPartnerId) {
          setClient(state);
        } else {
          setPending(null);
        }
      })
      .catch(() => {
        if (!cancelled) setPending(null);
      });
    return () => {
      cancelled = true;
    };
  }, [pending, canReview]);

  const close = useCallback(() => {
    setPending(null);
    setClient(null);
  }, []);

  const share = useCallback(async () => {
    if (!pending) return;
    setBusy(true);
    try {
      await setApplicationVisibility(pending.module, pending.entryId, true);
      toast.success("Application is now shared with the referral portal");
    } catch (err) {
      toast.error(err?.response?.data?.errorMessage || "Could not share the application");
    } finally {
      setBusy(false);
      close();
    }
  }, [pending, close]);

  const open = Boolean(pending && client);
  const moduleLabel = MODULE_LABELS[pending?.module] || pending?.module || "application";

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
      <DialogTitle>Share with the referral portal?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          {client?.name || "This client"} was referred
          {client?.referral ? ` by ${client.referral}` : ""}. Show this {moduleLabel} application in
          their referral portal?
        </Typography>
        <Typography variant="caption" color="text.secondary" className="!mt-2 !block">
          You can change this any time from the client&apos;s Applications tab.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy}>
          Keep hidden
        </Button>
        <Button onClick={share} variant="contained" disabled={busy} autoFocus>
          {busy ? "Sharing…" : "Share"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
